import { ReactNode } from "react";
import styled, { css } from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";

const { bannerTone, radius, typeScale } = adminTokens;
const [, , , spaceMd, spaceLg] = adminTokens.spacing;
const iconSize = 20;
// The design's banners sit the icon 14px from the text.
const iconGap = 14;
const titleFontSize = "15px";

export type BannerTone = keyof typeof bannerTone;

// On a phone the action wraps below the text, across the banner (PHONE-R30).
const Container = styled.div<{ $tone: BannerTone; $isPhone: boolean }>`
  display: flex;
  flex-wrap: ${({ $isPhone }) => ($isPhone ? "wrap" : "nowrap")};
  align-items: flex-start;
  gap: ${iconGap}px;
  box-sizing: border-box;
  padding: ${spaceMd}px ${spaceLg}px;
  border: 1px solid ${({ $tone }) => bannerTone[$tone].border};
  border-radius: ${radius.card}px;
  background-color: ${({ $tone }) => bannerTone[$tone].background};
  color: ${({ $tone }) => bannerTone[$tone].text};
  overflow-wrap: anywhere;
`;

const Icon = styled.svg`
  flex: none;
  margin-top: 1px;
`;

const Text = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Title = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${titleFontSize};
`;

const Body = styled.span`
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

const ActionSlot = styled.div<{ $isPhone: boolean }>`
  flex: ${({ $isPhone }) => ($isPhone ? "1 0 100%" : "none")};

  ${({ $isPhone }) =>
    $isPhone &&
    css`
      & > button {
        width: 100%;
      }
    `}
`;

// Glyph paths from the design's banners: a check, an exclamation, an "i".
const toneGlyph: Record<BannerTone, string> = {
  success: "m8 12 3 3 5-6",
  info: "M12 11v5M12 7.5h.01",
  warning: "M12 7v6M12 16.5h.01",
  error: "M12 7v6M12 16.5h.01",
};

type BannerAction = { label: string; onAction: () => void } & (
  | { isPending?: false; pendingLabel?: string }
  | { isPending: true; pendingLabel: string }
);

interface BannerProps {
  tone: BannerTone;
  title: ReactNode;
  body: ReactNode;
  action?: BannerAction;
}

const BannerActionButton = ({ tone, action }: { tone: BannerTone; action: BannerAction }) => {
  const { label, onAction, ...pendingState } = action;

  return (
    <Button look={tone === "error" ? "destructive" : "commit"} onClick={onAction} {...pendingState}>
      {label}
    </Button>
  );
};

// Sits above the content it describes: the title says what happened, the body
// what to do next, and an optional Button does it (BANNER-R1…R10). An error is
// announced at once; every other tone waits its turn.
export const Banner = ({ tone, title, body, action }: BannerProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <Container $tone={tone} $isPhone={isPhone} role={tone === "error" ? "alert" : "status"}>
      <Icon
        aria-hidden="true"
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d={toneGlyph[tone]} />
      </Icon>
      <Text>
        <Title>{title}</Title>
        <Body>{body}</Body>
      </Text>
      {action && (
        <ActionSlot $isPhone={isPhone}>
          <BannerActionButton tone={tone} action={action} />
        </ActionSlot>
      )}
    </Container>
  );
};
